import React, { useState, useEffect } from 'react';
import './ListaFichas.css';

const normalizar = (texto = '') => {
  return texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};

const ListaFichas = ({ 
  fichas = [], 
  carregando = false, 
  onPreencherFicha, 
  onNovaSimulacao,
  searchTerm = '',
  statusFilter = 'Todos os Status',
  highlightClienteNaLoja = true,
  exibirVendedor = false
}) => {
  const [menuAbertoId, setMenuAbertoId] = useState(null);

  useEffect(() => {
    if (!menuAbertoId) return undefined;

    const fecharMenu = (event) => {
      if (event.target instanceof Element && event.target.closest('[data-ficha-menu]')) return;
      setMenuAbertoId(null);
    };

    document.addEventListener('pointerdown', fecharMenu);
    return () => document.removeEventListener('pointerdown', fecharMenu);
  }, [menuAbertoId]);

  const formatarDataHora = (dataISO) => {
    const data = new Date(dataISO);
    if (Number.isNaN(data.getTime())) return '—';
    const dia = data.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    const horario = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return `${dia} · ${horario}`;
  };

  const formatarCpf = (cpf) => {
    const digitos = `${cpf || ''}`.replace(/\D/g, '').slice(0, 11);
    if (digitos.length !== 11) return cpf;
    return digitos.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const getStatusClass = (status) => {
    if (!status) return 'status-vazio';
    switch (status) {
      case 'Com possibilidade':
      case 'Aprovada':
        return 'status-aprovada';
      case 'Em análise':
        return 'status-em-analise';
      case 'Enviada para análise':
        return 'status-analise';
      case 'Recusada':
        return 'status-rejeitada';
      case 'Cancelada':
        return 'status-cancelada';
      default:
        return 'status-vazio';
    }
  };

  const fichasFiltradas = fichas.filter(ficha => {
    // Filtro de status
    if (statusFilter !== 'Todos os Status' && ficha.status !== statusFilter) {
      return false;
    }

    // Filtro de busca
    const termo = normalizar(searchTerm || '').trim();
    const termoDigitos = termo.replace(/\D/g, '');
    if (termo) {
      const nomeCliente = normalizar(ficha.cliente?.nome || '');
      const cpfCliente = (ficha.cliente?.cpf || '').replace(/\D/g, '');
      const nomeVendedor = normalizar(ficha.usuario_id?.nome || '');
      const fichaId = normalizar(ficha.fichaId || ficha._id || '');

      const cpfMatch = termoDigitos.length > 0 ? cpfCliente.includes(termoDigitos) : false;

      return nomeCliente.includes(termo)
        || cpfMatch
        || nomeVendedor.includes(termo)
        || fichaId.includes(termo);
    }

    return true;
  });

  // Ordenação: por data (mais recente primeiro)
  const fichasOrdenadas = [...fichasFiltradas].sort((a, b) => {
    return new Date(b.createdAt) - new Date(a.createdAt);
  });

  if (carregando) {
    return (
      <div className="lista-fichas-loading">
        <div className="spinner"></div>
        <p>Carregando fichas...</p>
      </div>
    );
  }

  if (fichasFiltradas.length === 0) {
    return (
      <div className="lista-fichas-vazia">
        <p>📋 Nenhuma ficha encontrada</p>
        <small>
          {searchTerm || statusFilter !== 'Todos os Status' 
            ? 'Tente ajustar os filtros de busca' 
            : 'Clique em "Nova Ficha" para criar a primeira ficha'}
        </small>
      </div>
    );
  }

  return (
    <div className="lista-fichas-container">
      <div className="lista-fichas-grid">
        {fichasOrdenadas.map((ficha) => (
          <div 
            key={ficha._id} 
            className="ficha-card"
            onClick={() => onNovaSimulacao && onNovaSimulacao(ficha)}
            onKeyDown={(e) => {
              if (e.currentTarget === e.target && (e.key === 'Enter' || e.key === ' ') && onNovaSimulacao) {
                e.preventDefault();
                onNovaSimulacao(ficha);
              }
            }}
            role="button"
            tabIndex={0}
            style={{
              backgroundColor: highlightClienteNaLoja && ficha.clienteNaLoja ? '#ffebee' : undefined,
              borderColor: highlightClienteNaLoja && ficha.clienteNaLoja ? '#f44336' : undefined
            }}
          >
            <div className="ficha-card-content">
              <div className="ficha-header">
                <div className="ficha-leading-placeholder" aria-hidden="true" />
                <div className="ficha-header-main">
                  <div className="ficha-data-line">
                    <span>{formatarDataHora(ficha.createdAt)}</span>
                  </div>
                  <div className="ficha-nome-line">
                    <div className="ficha-cliente-nome">
                      {ficha.cliente?.nome || 'Não informado'}
                    </div>
                    {ficha.status && (
                      <span className={`ficha-status ${getStatusClass(ficha.status)}`}>
                        {ficha.status}
                      </span>
                    )}
                  </div>
                  {ficha.cliente?.cpf && (
                    <div className="ficha-cliente">CPF: {formatarCpf(ficha.cliente.cpf)}</div>
                  )}
                  {exibirVendedor && (
                    <div className="ficha-vendedor">
                      {ficha.usuario_id?.nome || 'Não atribuído'}
                    </div>
                  )}
                </div>

                <div className="ficha-menu-container" data-ficha-menu onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="ficha-menu-trigger"
                    aria-label={`Ações da ficha de ${ficha.cliente?.nome || 'cliente não informado'}`}
                    aria-haspopup="menu"
                    aria-expanded={menuAbertoId === ficha._id}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuAbertoId((idAtual) => idAtual === ficha._id ? null : ficha._id);
                    }}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <circle cx="12" cy="5" r="2" />
                      <circle cx="12" cy="12" r="2" />
                      <circle cx="12" cy="19" r="2" />
                    </svg>
                  </button>

                  {menuAbertoId === ficha._id && (
                    <div className="ficha-menu-dropdown" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuAbertoId(null);
                          if (onPreencherFicha) onPreencherFicha(ficha);
                        }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0, color: 'currentColor' }}>
                          <path d="M3 17.25V21h3.75L19.81 7.94l-3.75-3.75L3 17.25z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                          <path d="M14.06 4.19l3.75 3.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                        <span><strong>Editar</strong></span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
            </div>
            
          </div>
        ))}
      </div>
    </div>
  );
};

export default ListaFichas;
